import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "./libs/leaflet";
import { DateTime } from "luxon";

const getMapboxToken = () => {
  const day = DateTime.now().day;

  return day <= 15
    ? process.env.NEXT_PUBLIC_MAPBOX_KEY
    : process.env.NEXT_PUBLIC_MAPBOX_KEY2;
};

const mapboxLayer = (style) => () => {
  return L.tileLayer(
    `https://api.mapbox.com/styles/v1/mapbox/${style}/tiles/{z}/{x}/{y}?access_token=${getMapboxToken()}`,
    {
      tileSize: 512,
      zoomOffset: -1,
      maxZoom: 26,
      keepBuffer: 5,
      updateWhenIdle: true,
    },
  );
};

// equivalente mapbox => streets-v12
const tileServerLayer = () => () =>
  L.tileLayer(`http://172.18.10.105:8080/styles/ecuador/512/{z}/{x}/{y}.png`, {
    tileSize: 512,
    zoomOffset: -1,
    maxZoom: 26,
    keepBuffer: 5,
    updateWhenIdle: true,
  });

const DEFAULT_LAYERS = [
  {
    id: "streets",
    label: "Predeterminado",
    default: true,
    createLayer: tileServerLayer(),
  },
  {
    id: "satellite-labels",
    label: "Satélite con etiquetas",
    createLayer: mapboxLayer("satellite-streets-v12"),
  },
];

const FULL_LAYERS = [
  ...DEFAULT_LAYERS,

  {
    id: "satellite",
    label: "Satélite",
    createLayer: mapboxLayer("satellite-v9"),
  },
  {
    id: "nav-day",
    label: "Navegación Día",
    createLayer: mapboxLayer("navigation-day-v1"),
  },
  {
    id: "nav-night",
    label: "Navegación Noche",
    createLayer: mapboxLayer("navigation-night-v1"),
  },
];

function resolveDefaultLayer({ presetLayers, externalLayers, baseLayerId }) {
  // 1) si te pasan un id explícito, manda eso
  // externas ganan si hay id repetido
  if (baseLayerId) {
    return (
      externalLayers.find((l) => l.id === baseLayerId) ||
      presetLayers.find((l) => l.id === baseLayerId) ||
      null
    );
  }

  // 2) si alguna externa viene marcada como default, gana
  const extDefault = externalLayers.find((l) => l.default);
  if (extDefault) return extDefault;

  // 3) si algún preset viene marcado default, usa ese
  const presetDefault = presetLayers.find((l) => l.default);
  if (presetDefault) return presetDefault;

  // 4) fallback
  return externalLayers[0] || presetLayers[0] || null;
}

export function BaseLayersControl({
  mode = "default", // "default" | "full"
  layers = [], // capas externas [{id,label,createLayer,default?}]
  defaultLayerId,
  collapsed = true,
  position = "topleft",
}) {
  const map = useMap();

  const controlRef = useRef(null);

  // { [label]: L.TileLayer }
  const layersRef = useRef({});

  // 🔥 persistir capa activa aunque el componente rerenderice
  const activeBaseLayerIdRef = useRef(null);

  // ⚠️ importante:
  // si layers cambia de referencia en cada render,
  // el control se recreará.
  // memoizar desde el padre.
  const presetLayers = useMemo(
    () => (mode === "full" ? FULL_LAYERS : DEFAULT_LAYERS),
    [mode],
  );

  // =========================
  // Escuchar cambios de capa
  // =========================
  useEffect(() => {
    if (!map) return;

    const handleBaseLayerChange = (e) => {
      const allLayers = [...presetLayers, ...(layers || [])];

      const found = allLayers.find((l) => l.label === e.name);

      if (found) {
        activeBaseLayerIdRef.current = found.id;
      }
    };

    map.on("baselayerchange", handleBaseLayerChange);

    return () => {
      map.off("baselayerchange", handleBaseLayerChange);
    };
  }, [map, presetLayers, layers]);

  // =========================
  // Crear control
  // =========================
  useEffect(() => {
    if (!map) return;

    const externalLayers = Array.isArray(layers) ? layers : [];

    const allLayers = [...presetLayers, ...externalLayers];

    // limpiar refs previas
    layersRef.current = {};

    // crear instancias leaflet
    allLayers.forEach((cfg) => {
      if (!cfg?.label || typeof cfg.createLayer !== "function") return;

      layersRef.current[cfg.label] = cfg.createLayer();
    });

    // 🔥 restaurar última capa activa
    const selectedLayerId = activeBaseLayerIdRef.current || defaultLayerId;

    const defaultCfg = resolveDefaultLayer({
      presetLayers,
      externalLayers,
      baseLayerId: selectedLayerId,
    });

    // agregar capa inicial
    if (defaultCfg?.label && layersRef.current[defaultCfg.label]) {
      layersRef.current[defaultCfg.label].addTo(map);
    }

    // crear control
    const control = L.control
      .layers(layersRef.current, null, {
        collapsed,
        position,
      })
      .addTo(map);

    controlRef.current = control;

    return () => {
      // remover control
      if (controlRef.current) {
        map.removeControl(controlRef.current);
      }

      // remover layers
      Object.values(layersRef.current).forEach((layer) => {
        try {
          map.removeLayer(layer);
        } catch (err) {
          console.error("Error al remover capa:", err);
        }
      });

      layersRef.current = {};
      controlRef.current = null;
    };
  }, [map, presetLayers, layers, defaultLayerId, collapsed, position]);

  return null;
}
